import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from "react"
import { getEvent } from "@/lib/api/client"
import { AuthError, toAsyncError } from "@/lib/api/errors"
import type { AsyncError } from "@/lib/api/errors"
import type {
  BookingState,
  BookingStep,
  PackageType,
  AirportOption,
  FlightOptionView,
  TravelerInfo,
} from "@/types/booking"
import { addOnQuantityLimit, addOnsForStay, getEventAddOns } from "@/lib/booking"
import type {
  AddOnOption,
  EventResponse,
  HotelOption,
  HotelOffersResponse,
  FlightOffersResponse,
} from "@/lib/api/types"

export type { AsyncError }

type BookingAction =
  | { type: "SET_STEP"; step: BookingStep }
  | {
      type: "SET_PACKAGE_TYPE"
      packageType: PackageType
      preserveResults?: boolean
    }
  | { type: "ADD_DEPARTURE_AIRPORT"; airport: AirportOption }
  | { type: "REMOVE_DEPARTURE_AIRPORT"; code: string }
  | { type: "SET_DESTINATION_AIRPORT"; airport: AirportOption }
  | { type: "CLEAR_DESTINATION_AIRPORT" }
  | { type: "SET_PASSENGER_COUNT"; count: number }
  | { type: "SET_STAY_PERIOD"; index: number }
  | { type: "SELECT_HOTEL"; hotel: HotelOption }
  | { type: "SELECT_FLIGHT"; flight: FlightOptionView }
  | { type: "SET_ADDON_QUANTITY"; addOnId: string; quantity: number }
  | { type: "UPDATE_TRAVELER_INFO"; info: Partial<TravelerInfo> }
  | { type: "SET_EVENT_LOADING" }
  | { type: "SET_EVENT_SUCCESS"; event: EventResponse }
  | { type: "SET_EVENT_ERROR"; error: AsyncError }
  | { type: "SET_OFFERS_LOADING" }
  | {
      type: "SET_OFFERS_SUCCESS"
      offers: HotelOffersResponse
    }
  | { type: "SET_OFFERS_ERROR"; error: AsyncError }
  | {
      type: "SET_FLIGHT_OFFERS_SUCCESS"
      flightOffers: FlightOffersResponse
      flightOptions: FlightOptionView[]
    }
  | { type: "SET_FLIGHT_OFFERS_ERROR"; error: AsyncError }
  | { type: "SET_HOTEL_ADDONS_LOADING" }
  | { type: "SET_HOTEL_ADDONS_SUCCESS"; hotelAddOns: Record<string, AddOnOption[]> }
  | { type: "SET_HOTEL_ADDONS_ERROR"; error: AsyncError }
  | { type: "SET_BOOKING_SUBMITTING"; submitting: boolean }
  | { type: "SET_BOOKING_ERROR"; error: AsyncError | null }
  | { type: "SET_ROOM_PREFERENCE"; sharedRoom: boolean; roomCapacity: 1 | 2 }
  | { type: "REFRESH_NEEDED" }
  | {
      type: "SET_PAYMENT_OPTION"
      paymentOption: "FULL" | "INSTALLMENTS_2" | "INSTALLMENTS_3"
    }
  | { type: "SET_CUSTOM_FLIGHT_DATES"; departureDate: string; returnDate: string }
  | { type: "CLEAR_CUSTOM_FLIGHT_DATES" }
  | { type: "RESET" }

const initialTravelerInfo: TravelerInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
}

/** Exported for tests: the state a fresh page starts from. */
export const initialState: BookingState = {
  currentStep: "idle",
  packageType: "flight-hotel",
  departureAirports: [],
  destinationAirport: null,
  passengerCount: 1,
  event: null,
  eventStatus: "idle",
  eventError: null,
  offers: null,
  hotelAddOns: {},
  hotelAddOnsStatus: "idle",
  flightOptions: [],
  offersStatus: "idle",
  offersError: null,
  flightOffersStatus: "idle",
  flightOffersError: null,
  selectedStayPeriodIndex: null,
  selectedHotel: null,
  userSelectedHotel: false,
  selectedFlight: null,
  userSelectedFlight: false,
  selectedAddOns: {},
  travelerInfo: initialTravelerInfo,
  currency: "EUR",
  isLoading: false,
  isSubmittingBooking: false,
  bookingError: null,
  sharedRoom: null,
  roomCapacity: null,
  needsOffersRefresh: false,
  selectedPaymentOption: "FULL",
  flightAllotmentId: null,
  flightBasePackagePrice: null,
  customFlightDepartureDate: null,
  customFlightReturnDate: null,
}

function clearResults(state: BookingState): BookingState {
  return {
    ...state,
    currentStep: "idle",
    offers: null,
    hotelAddOns: {},
    hotelAddOnsStatus: "idle",
    flightOptions: [],
    offersStatus: "idle",
    offersError: null,
    flightOffersStatus: "idle",
    flightOffersError: null,
    selectedHotel: null,
    userSelectedHotel: false,
    selectedFlight: null,
    userSelectedFlight: false,
    selectedAddOns: {},
    selectedPaymentOption: "FULL",
    bookingError: null,
    flightAllotmentId: null,
    flightBasePackagePrice: null,
    customFlightDepartureDate: state.customFlightDepartureDate,
    customFlightReturnDate: state.customFlightReturnDate,
  }
}

function sanitizeSelectedAddOns(
  selectedAddOns: Record<string, number>,
  availableAddOnIds: string[]
) {
  const allowedIds = new Set(availableAddOnIds)

  return Object.fromEntries(
    Object.entries(selectedAddOns).filter(([addOnId]) =>
      allowedIds.has(addOnId)
    )
  )
}

function withDefaultFreeAddOns(
  selectedAddOns: Record<string, number>,
  addOns: AddOnOption[],
  passengerCount: number
) {
  const next = { ...selectedAddOns }

  for (const addOn of addOns) {
    if (addOn.priceDeltaPerPerson !== 0) continue
    if (Object.hasOwn(next, addOn.id)) continue

    next[addOn.id] = passengerCount
  }

  return next
}

/** Exported for tests — a pure function of state and action. */
export function bookingReducer(
  state: BookingState,
  action: BookingAction
): BookingState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, currentStep: action.step }

    case "SET_PACKAGE_TYPE": {
      if (
        !action.preserveResults &&
        action.packageType === "flight-hotel" &&
        state.offers !== null
      ) {
        return {
          ...state,
          packageType: "flight-hotel",
          selectedFlight: null,
          userSelectedFlight: false,
          flightOffersStatus: "loading",
          currentStep:
            state.selectedHotel !== null
              ? "flight-selection"
              : state.currentStep,
        }
      }

      const nextState = {
        ...state,
        packageType: action.packageType,
        selectedFlight:
          action.packageType === "hotel-only" ? null : state.selectedFlight,
        selectedHotel:
          action.packageType === "flight-only" ? null : state.selectedHotel,
        userSelectedHotel:
          action.packageType === "flight-only" ? false : state.userSelectedHotel,
        sharedRoom:
          action.packageType === "flight-only" ? null : state.sharedRoom,
        roomCapacity:
          action.packageType === "flight-only" ? null : state.roomCapacity,
        customFlightDepartureDate:
          action.packageType === "flight-only" ? state.customFlightDepartureDate : null,
        customFlightReturnDate:
          action.packageType === "flight-only" ? state.customFlightReturnDate : null,
      }

      return action.preserveResults ? nextState : clearResults(nextState)
    }

    case "ADD_DEPARTURE_AIRPORT":
      if (state.departureAirports.length >= 3) return state
      if (
        state.departureAirports.some(
          (airport) => airport.code === action.airport.code
        )
      ) {
        return state
      }

      return clearResults({
        ...state,
        departureAirports: [...state.departureAirports, action.airport],
      })

    case "REMOVE_DEPARTURE_AIRPORT":
      return clearResults({
        ...state,
        departureAirports: state.departureAirports.filter(
          (airport) => airport.code !== action.code
        ),
      })

    case "SET_DESTINATION_AIRPORT":
      return { ...state, destinationAirport: action.airport }

    case "CLEAR_DESTINATION_AIRPORT":
      return { ...state, destinationAirport: null }

    case "SET_PASSENGER_COUNT":
      return clearResults({
        ...state,
        passengerCount: action.count,
        sharedRoom: action.count === 1 ? state.sharedRoom : null,
        roomCapacity: action.count === 1 ? state.roomCapacity : null,
        selectedAddOns: Object.fromEntries(
          Object.entries(state.selectedAddOns).map(([addOnId, quantity]) => [
            addOnId,
            Math.max(0, Math.min(quantity, action.count)),
          ])
        ),
      })

    case "SET_STAY_PERIOD":
      return clearResults({ ...state, selectedStayPeriodIndex: action.index, customFlightDepartureDate: null, customFlightReturnDate: null })

    case "SELECT_HOTEL": {
      const hotelAddOnIds = (state.hotelAddOns[action.hotel.hotelId] ?? []).map(
        (addOn) => addOn.id
      )
      const availableAddOnIds = [
        ...getEventAddOns(state).map((addOn) => addOn.id),
        ...hotelAddOnIds,
      ]

      return {
        ...state,
        selectedHotel: action.hotel,
        userSelectedHotel: true,
        selectedAddOns: withDefaultFreeAddOns(
          sanitizeSelectedAddOns(state.selectedAddOns, availableAddOnIds),
          [
            ...getEventAddOns(state),
            ...(state.hotelAddOns[action.hotel.hotelId] ?? []),
          ],
          state.passengerCount
        ),
      }
    }

    case "SELECT_FLIGHT":
      return {
        ...state,
        selectedFlight: action.flight,
        userSelectedFlight: true,
      }

    case "SET_ADDON_QUANTITY":
      return {
        ...state,
        selectedAddOns: {
          ...state.selectedAddOns,
          [action.addOnId]: Math.max(
            0,
            Math.min(action.quantity, addOnQuantityLimit(state, action.addOnId))
          ),
        },
      }

    case "UPDATE_TRAVELER_INFO":
      return {
        ...state,
        travelerInfo: { ...state.travelerInfo, ...action.info },
      }

    case "SET_EVENT_LOADING":
      return {
        ...state,
        eventStatus: "loading",
        eventError: null,
      }

    case "SET_EVENT_SUCCESS": {
      const periods = action.event.stayPeriods
      const startDate = action.event.startDate
      const endDate = action.event.endDate
      const mostPopularIndex =
        startDate && endDate
          ? periods.findIndex((p) => {
              const sStart = p.stayStartDate.slice(0, 10)
              const sEnd = p.stayEndDate.slice(0, 10)
              const eStart = startDate.slice(0, 10)
              const eEnd = endDate.slice(0, 10)
              return sStart === eStart && sEnd === eEnd
            })
          : -1

      const modeToPackageType: Record<string, PackageType> = {
        hotel: "hotel-only",
        hotel_flight: "flight-hotel",
        flight: "flight-only",
      }

      const supported = action.event.supportedModes
        .map((m) => modeToPackageType[m])
        .filter(Boolean) as PackageType[]

      const packageType = supported.includes(state.packageType)
        ? state.packageType
        : (supported[0] ?? state.packageType)

      const stayPeriodIndex =
        state.selectedStayPeriodIndex ??
        (periods.length > 0 ? (mostPopularIndex !== -1 ? mostPopularIndex : 0) : null)

      return {
        ...state,
        event: action.event,
        eventStatus: "success",
        eventError: null,
        packageType,
        selectedStayPeriodIndex: stayPeriodIndex,
        // The stay period is decided a few lines up, so the free add-ons picked
        // for the traveller have to be the ones on offer for it.
        selectedAddOns: withDefaultFreeAddOns(
          state.selectedAddOns,
          addOnsForStay(
            action.event.addOns,
            stayPeriodIndex === null ? null : (periods[stayPeriodIndex] ?? null)
          ),
          state.passengerCount
        ),
      }
    }

    case "SET_EVENT_ERROR":
      return {
        ...state,
        eventStatus: "error",
        eventError: action.error,
      }

    case "SET_OFFERS_LOADING":
      return {
        ...state,
        isLoading: true,
        needsOffersRefresh: false,
        offersStatus: "loading",
        offersError: null,
        flightOffersStatus: "idle",
        flightOffersError: null,
        bookingError: null,
      }

    case "SET_OFFERS_SUCCESS": {
      const includedHotel = action.offers.hotelOptions.find(
        (h) => h.priceDiffPerPerson === 0 && !h.soldOut
      )

      return {
        ...state,
        isLoading: false,
        offersStatus: "success",
        offersError: null,
        offers: action.offers,
        currency: action.offers.currency,
        flightOffersStatus:
          state.packageType === "flight-hotel" ? "loading" : "idle",
        flightOffersError: null,
        selectedHotel: includedHotel ?? null,
        userSelectedHotel: false,
        selectedAddOns: withDefaultFreeAddOns(
          state.selectedAddOns,
          getEventAddOns(state),
          state.passengerCount
        ),
      }
    }

    case "SET_OFFERS_ERROR":
      return {
        ...state,
        isLoading: false,
        offersStatus: "error",
        offersError: action.error,
      }

    case "SET_FLIGHT_OFFERS_SUCCESS": {
      const includedFlight =
        action.flightOptions.find((f) => f.priceDiffPerPerson === 0) ?? null

      const flightAllotmentId =
        action.flightOffers.allotmentId ?? state.flightAllotmentId

      const flightBasePackagePrice =
        state.packageType === "flight-only"
          ? action.flightOffers.basePackagePricePerPerson
          : state.flightBasePackagePrice

      return {
        ...state,
        isLoading: false,
        offersStatus: state.offersStatus === "loading" ? "success" : state.offersStatus,
        flightOptions: action.flightOptions,
        flightOffersStatus: "success",
        flightOffersError: null,
        flightAllotmentId,
        flightBasePackagePrice,
        offers: state.offers
          ? {
              ...state.offers,
              basePackagePricePerPerson:
                action.flightOffers.basePackagePricePerPerson,
              payLater: action.flightOffers.payLater ?? state.offers.payLater,
            }
          : state.packageType === "flight-only"
            ? {
                passengerCount: state.passengerCount,
                currency: state.currency,
                basePackagePricePerPerson:
                  action.flightOffers.basePackagePricePerPerson,
                hotelOptions: [],
                payLater: action.flightOffers.payLater ?? null,
              }
            : state.offers,
        selectedFlight: includedFlight,
        userSelectedFlight: false,
        selectedAddOns: withDefaultFreeAddOns(
          state.selectedAddOns,
          getEventAddOns(state),
          state.passengerCount
        ),
      }
    }

    case "SET_FLIGHT_OFFERS_ERROR":
      return {
        ...state,
        isLoading: false,
        offersStatus: state.offersStatus === "loading" ? "idle" : state.offersStatus,
        flightOffersStatus: "error",
        flightOffersError: action.error,
      }

    case "SET_HOTEL_ADDONS_LOADING":
      return {
        ...state,
        hotelAddOnsStatus: "loading",
      }

    case "SET_HOTEL_ADDONS_SUCCESS": {
      const selectedHotelAddOns = state.selectedHotel
        ? (action.hotelAddOns[state.selectedHotel.hotelId] ?? [])
        : []

      return {
        ...state,
        hotelAddOnsStatus: "success",
        hotelAddOns: action.hotelAddOns,
        selectedAddOns: withDefaultFreeAddOns(
          state.selectedAddOns,
          [...getEventAddOns(state), ...selectedHotelAddOns],
          state.passengerCount
        ),
      }
    }

    case "SET_HOTEL_ADDONS_ERROR":
      return {
        ...state,
        hotelAddOnsStatus: "error",
      }

    case "SET_BOOKING_SUBMITTING":
      return {
        ...state,
        isSubmittingBooking: action.submitting,
      }

    case "SET_BOOKING_ERROR":
      return {
        ...state,
        bookingError: action.error,
      }

    case "SET_ROOM_PREFERENCE":
      return {
        ...state,
        sharedRoom: action.sharedRoom,
        roomCapacity: action.roomCapacity,
      }

    case "REFRESH_NEEDED":
      return {
        ...state,
        needsOffersRefresh: true,
        flightOffersStatus: "idle",
        flightOffersError: null,
        hotelAddOns: {},
        hotelAddOnsStatus: "idle",
        selectedHotel: null,
        userSelectedHotel: false,
        selectedFlight: null,
        userSelectedFlight: false,
        selectedAddOns: {},
        selectedPaymentOption: "FULL",
        bookingError: null,
        flightAllotmentId: null,
        flightBasePackagePrice: null,
      }

    case "SET_PAYMENT_OPTION":
      return {
        ...state,
        selectedPaymentOption: action.paymentOption,
      }

    case "SET_CUSTOM_FLIGHT_DATES":
      return {
        ...state,
        customFlightDepartureDate: action.departureDate,
        customFlightReturnDate: action.returnDate,
        selectedStayPeriodIndex: null,
      }

    case "CLEAR_CUSTOM_FLIGHT_DATES":
      return {
        ...state,
        customFlightDepartureDate: null,
        customFlightReturnDate: null,
      }

    case "RESET":
      return {
        ...initialState,
        event: state.event,
        eventStatus: state.eventStatus,
        eventError: state.eventError,
        destinationAirport: state.destinationAirport,
        selectedStayPeriodIndex: state.event?.stayPeriods.length ? 0 : null,
      }

    default:
      return state
  }
}

interface BookingContextValue {
  state: BookingState
  dispatch: Dispatch<BookingAction>
}

const BookingContext = createContext<BookingContextValue | undefined>(undefined)

export function BookingProvider({
  children,
  slug,
}: {
  children: ReactNode
  slug?: string
}) {
  const [state, dispatch] = useReducer(bookingReducer, initialState)
  const resolvedSlug = slug

  useEffect(() => {
    let active = true

    async function loadEvent() {
      if (!resolvedSlug) {
        dispatch({
          type: "SET_EVENT_ERROR",
          error: toAsyncError(new Error("No event specified. Please check the link."), "event"),
        })
        return
      }

      dispatch({ type: "SET_EVENT_LOADING" })

      try {
        const event = await getEvent(resolvedSlug)
        if (!active) return
        dispatch({ type: "SET_EVENT_SUCCESS", event })
      } catch (error) {
        if (!active) return
        if (error instanceof AuthError) {
          window.location.href = "/login"
          return
        }
        dispatch({
          type: "SET_EVENT_ERROR",
          error: toAsyncError(error, "event"),
        })
      }
    }

    void loadEvent()

    return () => {
      active = false
    }
  }, [resolvedSlug])

  const value = useMemo(() => ({ state, dispatch }), [state])

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBooking() {
  const context = useContext(BookingContext)
  if (!context) {
    throw new Error("useBooking must be used within a BookingProvider")
  }
  return context
}
